import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  NoPermissionState,
  Notice,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Download, History, Scale, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { ReconcileCashSessionDialog } from '@/features/payments/CashDialogs';
import { CashMovementLabel } from '@/features/payments/CashMovementLabel';
import { cashSessionQueryOptions, cashSessionReportPdf } from '@/features/payments/queries';
import { cashSessionTone, movementTone } from '@/features/payments/status';
import { openDownloadedFile } from '@/lib/api';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateTime, formatTime } from '@/lib/format';

export const Route = createFileRoute('/_app/billing/cash/$sessionId')({
  component: CashSessionPage,
});

/** Fiche d'une session de caisse : journal complet et rapport PDF (SCR-093). */
function CashSessionPage() {
  const { sessionId } = Route.useParams();
  const { t, i18n } = useTranslation(['cash', 'common', 'errors']);
  const session = useSession();
  const canRead = session.can('cash.read');
  const [reconcileOpen, setReconcileOpen] = useState(false);

  const query = useQuery(cashSessionQueryOptions(sessionId, canRead));

  const report = useMutation({
    mutationFn: () => cashSessionReportPdf(sessionId),
    onSuccess: (file) => openDownloadedFile(file),
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={100} />
          <Skeleton height={320} />
        </div>
      </Container>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const item = query.data.session;
  const rows = query.data.movements;
  const variance = item.variance ?? 0;
  const needsReconcile = item.status === 'CLOSED' && variance !== 0 && item.reconciledAt === null;

  return (
    <Container>
      <PageHeader
        title={t('cash:detail.title', { date: formatDateTime(item.openedAt, i18n.language) })}
        subtitle={t('cash:detail.subtitle', { author: item.openedByName ?? '—' })}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('cash:title'), link: <Link to="/billing/cash">{t('cash:title')}</Link> },
          { label: formatDateTime(item.openedAt, i18n.language) },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            <Badge tone={cashSessionTone(item.status)} size="lg" dot>
              {t(`cash:status.${item.status}`)}
            </Badge>
            {item.reconciledAt ? (
              <Badge tone="violet" size="lg">
                {t('cash:status.reconciled')}
              </Badge>
            ) : null}
          </div>
        }
      />

      <Card className="mb-4">
        <Toolbar style={{ borderBottom: 0, borderRadius: 'var(--r-md)' }}>
          <span className="strong display t-16">
            {formatAmount(item.expectedBalance, i18n.language)}
          </span>
          <span className="spacer" />
          <Button
            variant="secondary"
            size="sm"
            loading={report.isPending}
            onClick={() => report.mutate()}
          >
            <Download className="ico ico-14" aria-hidden="true" />
            {t('cash:actions.downloadReport')}
          </Button>
          {needsReconcile && session.can('cash.reconcile') ? (
            <Button variant="primary" size="sm" onClick={() => setReconcileOpen(true)}>
              <Scale className="ico ico-14" aria-hidden="true" />
              {t('cash:actions.reconcile')}
            </Button>
          ) : null}
        </Toolbar>
      </Card>

      {needsReconcile ? (
        <Notice tone="danger" className="mb-4" title={t('cash:reconcile.pendingTitle')}>
          {t('cash:reconcile.pendingBody', {
            amount: formatAmount(variance, i18n.language),
            date: formatDateTime(item.closedAt ?? item.openedAt, i18n.language),
          })}
        </Notice>
      ) : null}

      {item.varianceJustification ? (
        <Notice tone="info" className="mb-4" title={t('cash:detail.justificationTitle')}>
          {t('cash:detail.justificationBody', {
            justification: item.varianceJustification,
            author: item.reconciledByName ?? '—',
          })}
        </Notice>
      ) : null}

      <div className="grid grid--main-aside">
        <Card>
          <CardHeader
            title={t('cash:detail.journalTitle')}
            icon={<History className="ico ico-16" aria-hidden="true" />}
            iconTone="blue"
          />
          <CardBody density="flush">
            {rows.length === 0 ? (
              <div className="card__body">
                <EmptyState
                  icon={<History className="ico ico-24" aria-hidden="true" />}
                  title={t('cash:movementsEmptyTitle')}
                />
              </div>
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('cash:columns.time')}</Th>
                      <Th>{t('cash:columns.kind')}</Th>
                      <Th>{t('cash:columns.description')}</Th>
                      <Th align="right">{t('cash:columns.amount')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((movement) => (
                      <Tr key={movement.id}>
                        <Td className="t-13" numeric>
                          {formatTime(movement.occurredAt, i18n.language)}
                          <div className="t-12 muted">{movement.createdByName ?? '—'}</div>
                        </Td>
                        <Td>
                          <Badge tone={movementTone(movement.kind)}>
                            {t(`cash:movementKind.${movement.kind}`)}
                          </Badge>
                        </Td>
                        <Td className="t-13">
                          <CashMovementLabel movement={movement} />
                        </Td>
                        <Td align="right" numeric>
                          <span
                            className="strong"
                            style={{
                              color:
                                movement.direction === 'OUT' ? 'var(--danger)' : 'var(--success)',
                            }}
                          >
                            {movement.direction === 'OUT' ? '−' : '+'}
                            {formatAmount(movement.amount, i18n.language)}
                          </span>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardBody>
        </Card>

        <aside className="stack sticky-aside">
          <Card>
            <CardHeader
              title={t('cash:detail.summaryTitle')}
              icon={<Wallet className="ico ico-16" aria-hidden="true" />}
              iconTone="amber"
            />
            <CardBody density="tight">
              <dl className="dl dl--lines t-13">
                <dt>{t('cash:fields.openingBalance')}</dt>
                <dd>{formatAmount(item.openingBalance, i18n.language)}</dd>
                <dt>{t('cash:fields.payments')}</dt>
                <dd>{formatAmount(item.totals.payments, i18n.language)}</dd>
                <dt>{t('cash:fields.ins')}</dt>
                <dd>{formatAmount(item.totals.ins, i18n.language)}</dd>
                <dt>{t('cash:fields.expenses')}</dt>
                <dd>{formatAmount(item.totals.expenses, i18n.language)}</dd>
                <dt>{t('cash:fields.outs')}</dt>
                <dd>{formatAmount(item.totals.outs, i18n.language)}</dd>
                <dt>{t('cash:fields.refunds')}</dt>
                <dd>{formatAmount(item.totals.refunds, i18n.language)}</dd>
                <dt>{t('cash:fields.expectedBalance')}</dt>
                <dd>
                  <b>{formatAmount(item.expectedBalance, i18n.language)}</b>
                </dd>
                <dt>{t('cash:fields.actualBalance')}</dt>
                <dd>
                  {item.actualBalance === null
                    ? '—'
                    : formatAmount(item.actualBalance, i18n.language)}
                </dd>
                <dt>{t('cash:fields.variance')}</dt>
                <dd>{item.variance === null ? '—' : formatAmount(item.variance, i18n.language)}</dd>
                <dt>{t('cash:columns.openedBy')}</dt>
                <dd>{item.openedByName ?? '—'}</dd>
                <dt>{t('cash:fields.closedBy')}</dt>
                <dd>{item.closedByName ?? '—'}</dd>
              </dl>
              {item.notes ? <p className="t-13 mt-3 mb-0">{item.notes}</p> : null}
            </CardBody>
          </Card>
        </aside>
      </div>

      <ReconcileCashSessionDialog
        open={reconcileOpen}
        onOpenChange={setReconcileOpen}
        session={item}
      />
    </Container>
  );
}
